import { homeFor } from "./homeFor";
import { monthInputValue, parseMonthInput } from "./ledgerLabels";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type ShareTarget =
  | { kind: "assembly"; id: string }
  | { kind: "ledger"; year: number; month: number };

export type ShareOutcome = "shared" | "copied" | "dismissed" | "failed";

export function assemblySharePath(id: string): string {
  return `/ir/asambleas/${id}`;
}

export function ledgerSharePath(year: number, month: number): string {
  return `/ir/cuentas?mes=${monthInputValue(year, month)}`;
}

export function parseShareTarget(pathWithSearch: string): ShareTarget | null {
  if (!pathWithSearch.startsWith("/") || pathWithSearch.startsWith("//")) return null;
  if (pathWithSearch.includes("\\") || pathWithSearch.includes("@")) return null;

  let url: URL;
  try {
    url = new URL(pathWithSearch, "https://zentrani.invalid");
  } catch {
    return null;
  }
  if (url.origin !== "https://zentrani.invalid") return null;
  if (url.hash) return null;

  const assemblyMatch = /^\/ir\/asambleas\/([^/]+)$/.exec(url.pathname);
  if (assemblyMatch) {
    const id = assemblyMatch[1];
    if (!UUID_RE.test(id)) return null;
    if ([...url.searchParams.keys()].length > 0) return null;
    return { kind: "assembly", id: id.toLowerCase() };
  }

  if (url.pathname === "/ir/cuentas") {
    const keys = [...url.searchParams.keys()];
    if (keys.length !== 1 || keys[0] !== "mes") return null;
    const parsed = parseMonthInput(url.searchParams.get("mes") ?? "");
    if (!parsed) return null;
    return { kind: "ledger", year: parsed.year, month: parsed.month };
  }

  return null;
}

export function isSafeShareNext(next: string | null | undefined): next is string {
  return Boolean(next && parseShareTarget(next));
}

export function resolveSharePath(role: string, pathWithSearch: string): string {
  const target = parseShareTarget(pathWithSearch);
  if (!target) return homeFor(role);

  if (role === "admin") {
    return target.kind === "assembly"
      ? `/admin/asambleas/${target.id}`
      : `/admin/cuentas?mes=${monthInputValue(target.year, target.month)}`;
  }
  if (role === "resident") {
    return target.kind === "assembly"
      ? `/asambleas/${target.id}`
      : `/cuentas?mes=${monthInputValue(target.year, target.month)}`;
  }
  return homeFor(role);
}

export function absoluteShareUrl(path: string, origin = globalThis.location?.origin ?? ""): string {
  return `${origin}${path}`;
}

type ShareIo = {
  share?: (data: { title: string; url: string }) => Promise<void>;
  writeText?: (text: string) => Promise<void>;
};

function defaultShareIo(): ShareIo {
  const nav = typeof navigator === "undefined" ? undefined : navigator;
  return {
    share: nav?.share ? (data) => nav.share(data) : undefined,
    writeText: nav?.clipboard?.writeText ? (text) => nav.clipboard.writeText(text) : undefined,
  };
}

export async function shareOrCopyUrl(url: string, title: string, io: ShareIo = defaultShareIo()): Promise<ShareOutcome> {
  if (io.share) {
    try {
      await io.share({ title, url });
      return "shared";
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return "dismissed";
    }
  }
  if (!io.writeText) return "failed";
  try {
    await io.writeText(url);
    return "copied";
  } catch {
    return "failed";
  }
}
